/**
 * AX1 Semantic Icon Map — one concept → one governed icon name.
 *
 * Use semantic names in nav config, status registries, and entity chips.
 * Prefer `getSemanticIcon('project')` or the alias directly (`name="project"`)
 * over ad-hoc lucide names (`folderOpen`, `folderKanban`, `briefcase`).
 */
import type { IconName } from './IconRegistry';

/** Platform entity and surface concepts with a dedicated icon. */
export type SemanticConcept =
  | 'dashboard'
  | 'project'
  | 'company'
  | 'investment'
  | 'task'
  | 'milestone'
  | 'document'
  | 'documentFolder'
  | 'dataRoom'
  | 'report'
  | 'finance'
  | 'capital'
  | 'vault'
  | 'security'
  | 'role'
  | 'governance'
  | 'decision'
  | 'approval'
  | 'verification'
  | 'evidence'
  | 'chat'
  | 'notification'
  | 'user'
  | 'team'
  | 'calendar'
  | 'settings'
  | 'escalation'
  | 'industry'
  | 'department';

/**
 * Canonical concept → icon bindings.
 * Each concept maps to exactly one IconName; each primary icon serves one concept.
 */
export const SEMANTIC_ICON_MAP: Record<SemanticConcept, IconName> = {
  dashboard: 'layoutDashboard',
  project: 'folderOpen',
  company: 'building2',
  investment: 'briefcase',
  task: 'checkSquare',
  milestone: 'flag',
  document: 'fileText',
  documentFolder: 'folder',
  dataRoom: 'package',
  report: 'fileText',
  finance: 'dollarSign',
  capital: 'circleDollarSign',
  vault: 'vault',
  security: 'shield',
  role: 'userCog',
  governance: 'scale',
  decision: 'scale',
  approval: 'stamp',
  verification: 'shieldCheck',
  evidence: 'fileCheck',
  chat: 'messageSquare',
  notification: 'bell',
  user: 'user',
  team: 'users',
  calendar: 'calendar',
  settings: 'settings',
  escalation: 'alertTriangle',
  industry: 'building',
  department: 'layers',
};

/** Resolve the governed icon name for a platform concept. */
export function getSemanticIcon(concept: SemanticConcept): IconName {
  return SEMANTIC_ICON_MAP[concept];
}
